import { evaluate_led_current } from './projectBuilderSuggestions';
import {
  getBreadboardBusGroupsFromMarkerKeys,
  isBreadboardComponent,
} from './projectBuilderBreadboard';

const ROLE_GROUPS = {
  power: new Set(['power-input', 'power-output', 'power-input-output', 'power']),
  signal: new Set([
    'gpio',
    'analog-input',
    'uart-rx',
    'uart-tx',
    'i2c-sda',
    'i2c-scl',
    'spi-mosi',
    'spi-miso',
    'spi-sck',
    'spi-cs',
    'reset',
    'enable',
    'flash-data',
    'special',
  ]),
};

const OUTPUT_TYPES = new Set(['output']);
const INPUT_TYPES = new Set(['input', 'analog_input']);
const DRIVEN_SIGNAL_TYPES = new Set(['output', 'open_drain', 'bidirectional']);

const RESTRICTION_LABELS = {
  limited_interrupt_support: 'Interrupt support is limited on this pin.',
  boot_sensitive: 'This pin affects boot mode and reset behavior.',
  onboard_led: 'This pin is also connected to an onboard LED.',
  serial_programming: 'This pin is shared with serial programming or serial console traffic.',
  used_by_flash: 'This pin is typically used by onboard flash storage.',
  avoid_for_gpio: 'Avoid using this pin as a general-purpose signal line.',
};

const SEVERITY_ORDER = ['error', 'warning', 'suggestion', 'info', 'pass'];

const normalize_text = (value) => String(value || '').trim();
const normalize_key = (value) => normalize_text(value).toLowerCase();

const to_number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const confidence_rank = {
  low: 1,
  medium: 2,
  high: 3,
};

const get_lowest_confidence = (values) => {
  const normalized_values = values.filter(Boolean);

  if (normalized_values.length === 0) {
    return 'low';
  }

  return normalized_values.reduce((lowest, value) => {
    if (!lowest) {
      return value;
    }

    return confidence_rank[value] < confidence_rank[lowest] ? value : lowest;
  }, '');
};

const is_power_role = (role) => ROLE_GROUPS.power.has(role);
const is_signal_role = (role) => ROLE_GROUPS.signal.has(role);

const get_display_pin_name = (pin) => {
  return (
    normalize_text(pin?.display_name) ||
    normalize_text(pin?.pin_name) ||
    normalize_text(pin?.pin_number) ||
    'Pin'
  );
};

const get_component_name = (component) => {
  return normalize_text(component?.board?.name || component?.name) || 'Component';
};

const get_component_category = (component) => {
  return normalize_key(component?.board?.category || component?.category);
};

const is_microcontroller_like = (component) => {
  const category = get_component_category(component);
  const board_name = normalize_key(get_component_name(component));

  return (
    category.includes('microcontroller') ||
    category.includes('development-board') ||
    board_name.includes('arduino') ||
    board_name.includes('esp32') ||
    board_name.includes('esp8266') ||
    board_name.includes('raspberry pi pico')
  );
};

const is_connectable_board_pin = (pin) => {
  return pin?.component_type === 'board' && pin?.has_canvas_marker;
};

const build_endpoint_key = (part_id, pin_key) => `${part_id}:${pin_key}`;

const add_pin_adjacency_edge = (adjacency, left_pin, right_pin) => {
  const left_id = String(left_pin?.id || '');
  const right_id = String(right_pin?.id || '');

  if (!left_id || !right_id || left_id === right_id) {
    return;
  }

  if (!adjacency.has(left_id)) {
    adjacency.set(left_id, new Set());
  }

  if (!adjacency.has(right_id)) {
    adjacency.set(right_id, new Set());
  }

  adjacency.get(left_id).add(right_id);
  adjacency.get(right_id).add(left_id);
};

const build_project_context = (project) => {
  const components = Array.isArray(project?.components) ? project.components : [];
  const connections = Array.isArray(project?.connections) ? project.connections : [];
  const pins = Array.isArray(project?.pins) ? project.pins : [];
  const component_by_id = new Map();
  const pin_by_id = new Map();
  const pin_by_endpoint = new Map();
  const direct_connections_by_pin_id = new Map();
  const electrical_adjacency_by_pin_id = new Map();
  const resolved_connections = [];

  components.forEach((component) => {
    component_by_id.set(component.id, component);
  });

  pins.forEach((pin) => {
    pin_by_id.set(String(pin.id), pin);
    pin_by_endpoint.set(build_endpoint_key(pin.component_id, pin.marker_key), pin);
  });

  connections.forEach((connection) => {
    const from_pin = pin_by_endpoint.get(build_endpoint_key(connection.from.partId, connection.from.pinKey));
    const to_pin = pin_by_endpoint.get(build_endpoint_key(connection.to.partId, connection.to.pinKey));

    if (!from_pin || !to_pin) {
      return;
    }

    const resolved_connection = {
      ...connection,
      from_pin,
      to_pin,
      from_component: component_by_id.get(connection.from.partId) || null,
      to_component: component_by_id.get(connection.to.partId) || null,
    };

    resolved_connections.push(resolved_connection);
    add_pin_adjacency_edge(electrical_adjacency_by_pin_id, from_pin, to_pin);

    [from_pin, to_pin].forEach((pin) => {
      const pin_id = String(pin.id);

      if (!direct_connections_by_pin_id.has(pin_id)) {
        direct_connections_by_pin_id.set(pin_id, []);
      }

      direct_connections_by_pin_id.get(pin_id).push(resolved_connection);
    });
  });

  components
    .filter(isBreadboardComponent)
    .forEach((component) => {
      const breadboard_pins = pins.filter((pin) => pin.component_id === component.id);
      const pin_by_marker_key = new Map(
        breadboard_pins.map((pin) => [String(pin.marker_key || ''), pin])
      );

      getBreadboardBusGroupsFromMarkerKeys(
        breadboard_pins.map((pin) => pin.marker_key)
      ).forEach((group) => {
        const [first_marker_key, ...rest_marker_keys] = group;
        const first_pin = pin_by_marker_key.get(first_marker_key);

        if (!first_pin) {
          return;
        }

        rest_marker_keys.forEach((marker_key) => {
          const next_pin = pin_by_marker_key.get(marker_key);

          if (next_pin) {
            add_pin_adjacency_edge(electrical_adjacency_by_pin_id, first_pin, next_pin);
          }
        });
      });
    });

  return {
    components,
    connections,
    pins,
    component_by_id,
    pin_by_id,
    pin_by_endpoint,
    direct_connections_by_pin_id,
    electrical_adjacency_by_pin_id,
    resolved_connections,
  };
};

const get_pin_connections = (context, pin) => {
  return context.direct_connections_by_pin_id.get(String(pin.id)) || [];
};

const get_connected_pins = (context, pin) => {
  const start_pin_id = String(pin?.id || '');

  if (!start_pin_id || !context?.electrical_adjacency_by_pin_id) {
    return [];
  }

  const visited = new Set([start_pin_id]);
  const queue = [start_pin_id];

  while (queue.length > 0) {
    const current_pin_id = queue.shift();
    const neighbors = context.electrical_adjacency_by_pin_id.get(current_pin_id);

    if (!neighbors) {
      continue;
    }

    neighbors.forEach((neighbor_pin_id) => {
      if (visited.has(neighbor_pin_id)) {
        return;
      }

      visited.add(neighbor_pin_id);
      queue.push(neighbor_pin_id);
    });
  }

  visited.delete(start_pin_id);

  return Array.from(visited)
    .map((pin_id) => context.pin_by_id.get(String(pin_id)))
    .filter(Boolean);
};

const pin_supports_capability = (pin, capability) => {
  const requested = normalize_key(capability);
  const role = normalize_key(pin?.pin_role);
  const tags = new Set((pin?.capability_tags || []).map((tag) => normalize_key(tag)));

  if (role === requested || tags.has(requested)) {
    return true;
  }

  if (requested === 'analog-input') {
    return role === 'analog-input' || tags.has('analog');
  }

  if (requested === 'power') {
    return is_power_role(role) || tags.has('power');
  }

  if (requested === 'gpio') {
    return role === 'gpio' || tags.has('gpio');
  }

  return false;
};

const get_voltage_range = (pin, component) => {
  const min = to_number(pin?.voltage_min_v);
  const max = to_number(pin?.voltage_max_v);

  if (min !== null || max !== null) {
    return { min, max };
  }

  const board_logic_min = to_number(component?.board?.logic_voltage_min_v);
  const board_logic_max = to_number(component?.board?.logic_voltage_max_v);

  if (is_signal_role(normalize_key(pin?.pin_role)) && board_logic_max !== null) {
    return {
      min: board_logic_min !== null ? board_logic_min : 0,
      max: board_logic_max,
    };
  }

  return { min: null, max: null };
};

const get_power_receiver_range = (pin, component) => {
  const pin_range = get_voltage_range(pin, component);

  if (pin_range.min !== null || pin_range.max !== null) {
    return pin_range;
  }

  return {
    min: to_number(component?.board?.supply_voltage_min_v),
    max: to_number(component?.board?.supply_voltage_max_v),
  };
};

const is_5v_tolerant = (pin) => {
  return pin?.is_5v_tolerant === true || pin?.is_5v_tolerant === 1;
};

const make_result_builder = () => {
  let counter = 0;

  return (payload) => ({
    id: payload.id || `analysis-${++counter}`,
    severity: payload.severity,
    confidence: payload.confidence || 'medium',
    category: payload.category,
    title: payload.title,
    message: payload.message,
    related_items: {
      component_ids: payload.related_items?.component_ids || [],
      pin_ids: payload.related_items?.pin_ids || [],
      wire_ids: payload.related_items?.wire_ids || [],
    },
  });
};

const sort_results = (results) => {
  return [...results].sort((left, right) => {
    const severity_diff = SEVERITY_ORDER.indexOf(left.severity) - SEVERITY_ORDER.indexOf(right.severity);

    if (severity_diff !== 0) {
      return severity_diff;
    }

    return left.title.localeCompare(right.title);
  });
};

const summarize_results = (results) => {
  return {
    error: results.filter((result) => result.severity === 'error').length,
    warning: results.filter((result) => result.severity === 'warning').length,
    suggestion: results.filter((result) => result.severity === 'suggestion').length,
    info: results.filter((result) => result.severity === 'info').length,
    pass: results.filter((result) => result.severity === 'pass').length,
  };
};

const format_restriction_text = (pin) => {
  const restriction_messages = (pin?.restrictions || [])
    .map((restriction) => RESTRICTION_LABELS[restriction] || `${restriction.replace(/_/g, ' ')}.`)
    .filter(Boolean);
  const usage_notes = (pin?.safe_usage_notes || []).filter(Boolean);

  return [...restriction_messages, ...usage_notes].join(' ');
};

const add_board_confidence_results = (context, push_result, results) => {
  context.components
    .filter((component) => component.type === 'board')
    .forEach((component) => {
      const board_pins = context.pins.filter((pin) =>
        pin.component_id === component.id && is_connectable_board_pin(pin)
      );

      const inferred_pin_count = board_pins.filter((pin) =>
        ['inferred', 'default'].includes(pin?.inference?.pin_role_source)
      ).length;
      const profile_pin_count = board_pins.filter((pin) =>
        pin?.inference?.profile_applied
      ).length;

      if (inferred_pin_count === 0 && profile_pin_count === 0) {
        return;
      }

      const parts = [];

      if (profile_pin_count > 0) {
        parts.push(`${profile_pin_count} pin override${profile_pin_count === 1 ? '' : 's'} came from a board profile`);
      }

      if (inferred_pin_count > 0) {
        parts.push(`${inferred_pin_count} pin role${inferred_pin_count === 1 ? '' : 's'} were inferred`);
      }

      results.push(push_result({
        severity: 'info',
        confidence: component?.board?.analysis_confidence || 'medium',
        category: 'completeness',
        title: `${get_component_name(component)} analysis used normalized metadata`,
        message: `${get_component_name(component)} used normalized pin metadata before analysis. ${parts.join(', ')}.`,
        related_items: {
          component_ids: [component.id],
          pin_ids: board_pins.map((pin) => String(pin.id)),
          wire_ids: [],
        },
      }));
    });
};

const add_power_and_ground_results = (context, push_result, results) => {
  context.components
    .filter((component) => component.type === 'board')
    .forEach((component) => {
      const board_pins = context.pins.filter((pin) =>
        pin.component_id === component.id && is_connectable_board_pin(pin)
      );
      const ground_pins = board_pins.filter((pin) => normalize_key(pin.pin_role) === 'ground');
      const power_input_pins = board_pins.filter((pin) =>
        ['power-input', 'power-input-output'].includes(normalize_key(pin.pin_role))
      );
      const connected_ground_pins = ground_pins.filter((pin) => get_pin_connections(context, pin).length > 0);
      const connected_power_pins = power_input_pins.filter((pin) => get_pin_connections(context, pin).length > 0);
      const board_is_controller = is_microcontroller_like(component);

      if (ground_pins.length > 0) {
        if (connected_ground_pins.length === 0) {
          results.push(push_result({
            severity: board_is_controller ? 'warning' : 'error',
            confidence: get_lowest_confidence(ground_pins.map((pin) => pin?.inference?.pin_role_confidence)),
            category: 'completeness',
            title: `${get_component_name(component)} has no ground connection`,
            message: board_is_controller
              ? `${get_component_name(component)} does not have an explicit GND connection in the project. If it will be powered separately, this may be okay.`
              : `${get_component_name(component)} does not appear to have a ground connection.`,
            related_items: {
              component_ids: [component.id],
              pin_ids: ground_pins.map((pin) => String(pin.id)),
              wire_ids: [],
            },
          }));
        } else {
          results.push(push_result({
            severity: 'pass',
            confidence: 'high',
            category: 'power',
            title: `${get_component_name(component)} ground is connected`,
            message: `${get_component_name(component)} has at least one GND connection.`,
            related_items: {
              component_ids: [component.id],
              pin_ids: connected_ground_pins.map((pin) => String(pin.id)),
              wire_ids: connected_ground_pins.flatMap((pin) =>
                get_pin_connections(context, pin).map((connection) => connection.id)
              ),
            },
          }));
        }
      }

      if (power_input_pins.length > 0) {
        if (connected_power_pins.length === 0) {
          results.push(push_result({
            severity: board_is_controller ? 'warning' : 'error',
            confidence: get_lowest_confidence(power_input_pins.map((pin) => pin?.inference?.pin_role_confidence)),
            category: 'completeness',
            title: `${get_component_name(component)} has no power-input connection`,
            message: board_is_controller
              ? `${get_component_name(component)} does not have an explicit power-input connection in the project. If it will be USB-powered separately, this may be okay.`
              : `${get_component_name(component)} does not appear to have a connected power input.`,
            related_items: {
              component_ids: [component.id],
              pin_ids: power_input_pins.map((pin) => String(pin.id)),
              wire_ids: [],
            },
          }));
        } else {
          results.push(push_result({
            severity: 'pass',
            confidence: 'high',
            category: 'power',
            title: `${get_component_name(component)} power input is connected`,
            message: `${get_component_name(component)} has at least one power-input connection.`,
            related_items: {
              component_ids: [component.id],
              pin_ids: connected_power_pins.map((pin) => String(pin.id)),
              wire_ids: connected_power_pins.flatMap((pin) =>
                get_pin_connections(context, pin).map((connection) => connection.id)
              ),
            },
          }));
        }
      }
    });
};

const add_restricted_pin_results = (context, push_result, results) => {
  context.pins
    .filter((pin) =>
      is_connectable_board_pin(pin) &&
      (pin?.restrictions?.length > 0 || pin?.safe_usage_notes?.length > 0) &&
      get_pin_connections(context, pin).length > 0
    )
    .forEach((pin) => {
      const component = context.component_by_id.get(pin.component_id);
      const message = format_restriction_text(pin);
      const wire_ids = get_pin_connections(context, pin).map((connection) => connection.id);

      results.push(push_result({
        severity: 'warning',
        confidence: pin?.inference?.profile_applied ? 'high' : (pin?.inference?.pin_role_confidence || 'medium'),
        category: 'pin-warning',
        title: `${get_component_name(component)} ${get_display_pin_name(pin)} has usage restrictions`,
        message: message || `${get_display_pin_name(pin)} has special usage notes.`,
        related_items: {
          component_ids: [pin.component_id],
          pin_ids: [String(pin.id)],
          wire_ids,
        },
      }));
    });
};

const add_connection_safety_results = (context, push_result, results) => {
  const protocol_pass_keys = new Set();

  context.resolved_connections.forEach((connection) => {
    const connection_pins = [connection.from_pin, connection.to_pin];
    const connection_confidence = get_lowest_confidence(
      connection_pins.map((pin) => pin?.inference?.pin_role_confidence)
    );
    const from_role = normalize_key(connection.from_pin.pin_role);
    const to_role = normalize_key(connection.to_pin.pin_role);
    const from_type = normalize_key(connection.from_pin.electrical_type);
    const to_type = normalize_key(connection.to_pin.electrical_type);
    const wire_id = connection.id;

    if (from_type === 'output' && to_type === 'output') {
      results.push(push_result({
        severity: 'error',
        confidence: connection_confidence,
        category: 'signal',
        title: 'Direct output-to-output conflict detected',
        message: `${get_component_name(connection.from_component)} ${get_display_pin_name(connection.from_pin)} is connected directly to ${get_component_name(connection.to_component)} ${get_display_pin_name(connection.to_pin)}, and both pins appear to drive outputs.`,
        related_items: {
          component_ids: [connection.from_pin.component_id, connection.to_pin.component_id],
          pin_ids: [String(connection.from_pin.id), String(connection.to_pin.id)],
          wire_ids: [wire_id],
        },
      }));
    }

    const from_is_power_output = from_role === 'power-output';
    const to_is_power_output = to_role === 'power-output';
    const from_is_power_input = ['power-input', 'power-input-output'].includes(from_role);
    const to_is_power_input = ['power-input', 'power-input-output'].includes(to_role);
    const from_exact_voltage = (() => {
      const range = get_voltage_range(connection.from_pin, connection.from_component);
      return range.min !== null && range.max !== null && range.min === range.max ? range.max : null;
    })();
    const to_exact_voltage = (() => {
      const range = get_voltage_range(connection.to_pin, connection.to_component);
      return range.min !== null && range.max !== null && range.min === range.max ? range.max : null;
    })();

    if (from_is_power_output && to_is_power_output && from_exact_voltage !== null && to_exact_voltage !== null) {
      if (Math.abs(from_exact_voltage - to_exact_voltage) > 0.25) {
        results.push(push_result({
          severity: 'error',
          confidence: connection_confidence,
          category: 'power',
          title: 'Different power rails are tied together',
          message: `${get_component_name(connection.from_component)} ${get_display_pin_name(connection.from_pin)} (${from_exact_voltage}V) is connected directly to ${get_component_name(connection.to_component)} ${get_display_pin_name(connection.to_pin)} (${to_exact_voltage}V).`,
          related_items: {
            component_ids: [connection.from_pin.component_id, connection.to_pin.component_id],
            pin_ids: [String(connection.from_pin.id), String(connection.to_pin.id)],
            wire_ids: [wire_id],
          },
        }));
      }
    }

    const evaluate_power_pair = (source_pin, target_pin, source_component, target_component) => {
      const source_range = get_voltage_range(source_pin, source_component);
      const target_range = get_power_receiver_range(target_pin, target_component);
      const exact_source_voltage =
        source_range.min !== null &&
        source_range.max !== null &&
        source_range.min === source_range.max
          ? source_range.max
          : null;

      if (exact_source_voltage === null) {
        return;
      }

      if (target_range.max !== null && exact_source_voltage > target_range.max + 0.1) {
        results.push(push_result({
          severity: 'error',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'power',
          title: 'Power voltage exceeds the receiving pin range',
          message: `${get_component_name(target_component)} ${get_display_pin_name(target_pin)} is connected to ${exact_source_voltage}V from ${get_component_name(source_component)} ${get_display_pin_name(source_pin)}, which appears higher than its supported input range.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
        return;
      }

      if (target_range.min !== null && exact_source_voltage < target_range.min - 0.1) {
        results.push(push_result({
          severity: 'warning',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'power',
          title: 'Power voltage may be too low for the receiving pin',
          message: `${get_component_name(target_component)} ${get_display_pin_name(target_pin)} is connected to ${exact_source_voltage}V, which may be below its expected input range.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
        return;
      }

      if (exact_source_voltage >= 4.5 && target_range.max === null) {
        results.push(push_result({
          severity: 'warning',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'power',
          title: 'Confirm 5V power input compatibility',
          message: `${get_component_name(target_component)} ${get_display_pin_name(target_pin)} is connected to 5V. Confirm that this input is rated for 5V.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
        return;
      }

      results.push(push_result({
        severity: 'pass',
        confidence: get_lowest_confidence([
          source_pin?.inference?.voltage_confidence,
          target_pin?.inference?.voltage_confidence,
        ]),
        category: 'power',
        title: 'Power connection voltage looks reasonable',
        message: `${get_component_name(target_component)} ${get_display_pin_name(target_pin)} is connected to a plausible supply voltage from ${get_component_name(source_component)} ${get_display_pin_name(source_pin)}.`,
        related_items: {
          component_ids: [source_pin.component_id, target_pin.component_id],
          pin_ids: [String(source_pin.id), String(target_pin.id)],
          wire_ids: [wire_id],
        },
      }));
    };

    if (from_is_power_output && to_is_power_input) {
      evaluate_power_pair(connection.from_pin, connection.to_pin, connection.from_component, connection.to_component);
    }

    if (to_is_power_output && from_is_power_input) {
      evaluate_power_pair(connection.to_pin, connection.from_pin, connection.to_component, connection.from_component);
    }

    const signal_range_pairs = [
      {
        source_pin: connection.from_pin,
        target_pin: connection.to_pin,
        source_component: connection.from_component,
        target_component: connection.to_component,
      },
      {
        source_pin: connection.to_pin,
        target_pin: connection.from_pin,
        source_component: connection.to_component,
        target_component: connection.from_component,
      },
    ];

    signal_range_pairs.forEach(({ source_pin, target_pin, source_component, target_component }) => {
      const source_role = normalize_key(source_pin.pin_role);
      const target_role = normalize_key(target_pin.pin_role);
      const source_type = normalize_key(source_pin.electrical_type);
      const target_type = normalize_key(target_pin.electrical_type);

      if (!is_signal_role(source_role) || !is_signal_role(target_role)) {
        return;
      }

      if (!DRIVEN_SIGNAL_TYPES.has(source_type) && !OUTPUT_TYPES.has(source_type)) {
        return;
      }

      if (!INPUT_TYPES.has(target_type) && target_type !== 'bidirectional' && target_type !== 'open_drain' && target_type !== 'special') {
        return;
      }

      const source_range = get_voltage_range(source_pin, source_component);
      const target_range = get_voltage_range(target_pin, target_component);

      if (source_range.max === null) {
        return;
      }

      if (target_range.max !== null && source_range.max > target_range.max + 0.1) {
        results.push(push_result({
          severity: 'error',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'signal',
          title: 'Signal voltage exceeds the receiving pin range',
          message: `${get_component_name(source_component)} ${get_display_pin_name(source_pin)} may drive up to ${source_range.max}V, but ${get_component_name(target_component)} ${get_display_pin_name(target_pin)} appears limited to ${target_range.max}V.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
        return;
      }

      if (source_range.max >= 4.5 && target_range.max !== null && target_range.max <= 3.6 && !is_5v_tolerant(target_pin)) {
        results.push(push_result({
          severity: 'error',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'signal',
          title: '5V logic may be feeding a non-5V-tolerant pin',
          message: `${get_component_name(source_component)} ${get_display_pin_name(source_pin)} appears to be a 5V-level signal, but ${get_component_name(target_component)} ${get_display_pin_name(target_pin)} is not marked 5V tolerant.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
      } else if (source_range.max >= 4.5 && target_range.max !== null && target_range.max <= 3.6 && target_pin.is_5v_tolerant === null) {
        results.push(push_result({
          severity: 'warning',
          confidence: get_lowest_confidence([
            source_pin?.inference?.voltage_confidence,
            target_pin?.inference?.voltage_confidence,
          ]),
          category: 'signal',
          title: 'Confirm 5V tolerance on the receiving pin',
          message: `${get_component_name(source_component)} ${get_display_pin_name(source_pin)} may be a 5V-level signal. Confirm that ${get_component_name(target_component)} ${get_display_pin_name(target_pin)} can tolerate that level.`,
          related_items: {
            component_ids: [source_pin.component_id, target_pin.component_id],
            pin_ids: [String(source_pin.id), String(target_pin.id)],
            wire_ids: [wire_id],
          },
        }));
      }
    });

    const protocol_rules = [
      {
        source_capability: 'i2c-sda',
        accepts: ['i2c-sda'],
        title: 'Possible I2C SDA pin mismatch',
      },
      {
        source_capability: 'i2c-scl',
        accepts: ['i2c-scl'],
        title: 'Possible I2C SCL pin mismatch',
      },
      {
        source_capability: 'spi-mosi',
        accepts: ['spi-mosi'],
        title: 'Possible SPI MOSI pin mismatch',
      },
      {
        source_capability: 'spi-miso',
        accepts: ['spi-miso'],
        title: 'Possible SPI MISO pin mismatch',
      },
      {
        source_capability: 'spi-sck',
        accepts: ['spi-sck'],
        title: 'Possible SPI SCK pin mismatch',
      },
      {
        source_capability: 'spi-cs',
        accepts: ['spi-cs', 'gpio'],
        title: 'Possible SPI CS pin mismatch',
      },
      {
        source_capability: 'uart-tx',
        accepts: ['uart-rx'],
        title: 'Possible UART TX/RX mismatch',
      },
      {
        source_capability: 'uart-rx',
        accepts: ['uart-tx'],
        title: 'Possible UART RX/TX mismatch',
      },
      {
        source_capability: 'analog-input',
        accepts: ['analog-input'],
        title: 'Possible analog pin mismatch',
      },
    ];

    protocol_rules.forEach((rule) => {
      [
        {
          source_pin: connection.from_pin,
          target_pin: connection.to_pin,
          source_component: connection.from_component,
          target_component: connection.to_component,
        },
        {
          source_pin: connection.to_pin,
          target_pin: connection.from_pin,
          source_component: connection.to_component,
          target_component: connection.from_component,
        },
      ].forEach(({ source_pin, target_pin, source_component, target_component }) => {
        const pass_key = `${connection.id}:${rule.source_capability}:${source_pin.id}:${target_pin.id}`;

        if (!pin_supports_capability(source_pin, rule.source_capability)) {
          return;
        }

        const matches = rule.accepts.some((capability) => pin_supports_capability(target_pin, capability));

        if (!matches) {
          results.push(push_result({
            severity: 'warning',
            confidence: get_lowest_confidence([
              source_pin?.inference?.pin_role_confidence,
              target_pin?.inference?.pin_role_confidence,
            ]),
            category: 'protocol',
            title: rule.title,
            message: `${get_component_name(source_component)} ${get_display_pin_name(source_pin)} is connected to ${get_component_name(target_component)} ${get_display_pin_name(target_pin)}, but the receiving pin is not marked for ${rule.source_capability}.`,
            related_items: {
              component_ids: [source_pin.component_id, target_pin.component_id],
              pin_ids: [String(source_pin.id), String(target_pin.id)],
              wire_ids: [wire_id],
            },
          }));
          return;
        }

        if (!protocol_pass_keys.has(pass_key)) {
          protocol_pass_keys.add(pass_key);
          results.push(push_result({
            severity: 'pass',
            confidence: get_lowest_confidence([
              source_pin?.inference?.pin_role_confidence,
              target_pin?.inference?.pin_role_confidence,
            ]),
            category: 'protocol',
            title: `${rule.source_capability} connection matches pin capabilities`,
            message: `${get_component_name(source_component)} ${get_display_pin_name(source_pin)} is connected to a compatible ${rule.source_capability} destination.`,
            related_items: {
              component_ids: [source_pin.component_id, target_pin.component_id],
              pin_ids: [String(source_pin.id), String(target_pin.id)],
              wire_ids: [wire_id],
            },
          }));
        }
      });
    });
  });
};

const add_led_results = (context, push_result, results) => {
  context.components
    .filter((component) => component.type === 'passive' && component.passiveType === 'led')
    .forEach((component) => {
      const led_pins = context.pins.filter((pin) => pin.component_id === component.id);
      const led_connections = led_pins.flatMap((pin) => get_pin_connections(context, pin));
      const unique_led_connections = Array.from(new Map(
        led_connections.map((connection) => [connection.id, connection])
      ).values());

      if (unique_led_connections.length === 0) {
        return;
      }

      const directly_connected_pins = led_pins.flatMap((pin) => get_connected_pins(context, pin));
      const resistor_pins = directly_connected_pins.filter((pin) => pin?.passive_type === 'resistor');

      if (resistor_pins.length === 0) {
        results.push(push_result({
          severity: 'warning',
          confidence: 'high',
          category: 'current',
          title: `${get_component_name(component)} appears to be missing a series resistor`,
          message: `${get_component_name(component)} is connected without a directly connected resistor. Add a current-limiting resistor in series with the LED.`,
          related_items: {
            component_ids: [component.id, ...directly_connected_pins.map((pin) => pin.component_id)],
            pin_ids: [...led_pins.map((pin) => String(pin.id)), ...directly_connected_pins.map((pin) => String(pin.id))],
            wire_ids: unique_led_connections.map((connection) => connection.id),
          },
        }));
        return;
      }

      results.push(push_result({
        severity: 'pass',
        confidence: 'high',
        category: 'current',
        title: `${get_component_name(component)} includes a series resistor`,
        message: `${get_component_name(component)} has a resistor connected directly in the LED path.`,
        related_items: {
          component_ids: [component.id, ...resistor_pins.map((pin) => pin.component_id)],
          pin_ids: [...led_pins.map((pin) => String(pin.id)), ...resistor_pins.map((pin) => String(pin.id))],
          wire_ids: unique_led_connections.map((connection) => connection.id),
        },
      }));

      const resistor_pin = resistor_pins[0];
      const gpio_pin = get_connected_pins(context, resistor_pin)
        .find((pin) =>
          pin.component_id !== component.id &&
          pin.component_id !== resistor_pin.component_id &&
          is_signal_role(normalize_key(pin.pin_role))
        );
      const led_pin = led_pins.find((pin) => normalize_key(pin.pin_role) === 'led-anode') || led_pins[0];

      if (gpio_pin && led_pin) {
        const evaluation = evaluate_led_current({
          gpio_pin,
          resistor_pin,
          led_pin,
        });

        if (evaluation && evaluation.valid === false) {
          results.push(push_result({
            severity: 'warning',
            confidence: evaluation.confidence || 'medium',
            category: 'current',
            title: `${get_component_name(component)} current estimate looks high`,
            message: evaluation.reason,
            related_items: {
              component_ids: [component.id, gpio_pin.component_id, resistor_pin.component_id],
              pin_ids: [String(led_pin.id), String(gpio_pin.id), String(resistor_pin.id)],
              wire_ids: [
                ...get_pin_connections(context, led_pin).map((connection) => connection.id),
                ...get_pin_connections(context, resistor_pin).map((connection) => connection.id),
              ],
            },
          }));
        }
      }
    });
};

export const analyze_circuit = (project) => {
  const context = build_project_context(project);
  const push_result = make_result_builder();
  const results = [];

  add_board_confidence_results(context, push_result, results);
  add_power_and_ground_results(context, push_result, results);
  add_restricted_pin_results(context, push_result, results);
  add_connection_safety_results(context, push_result, results);
  add_led_results(context, push_result, results);

  const sorted_results = sort_results(results);

  return {
    results: sorted_results,
    summary: summarize_results(sorted_results),
  };
};

const get_step_priority = (connection) => {
  if (connection.wireColorRole === 'power') return 1;
  if (connection.wireColorRole === 'ground') return 2;
  return 3;
};

const get_connection_label = (component, pin) => {
  return `${get_component_name(component)} ${get_display_pin_name(pin)}`.trim();
};

const order_step_endpoints = (connection) => {
  const from_component = connection.from_component;
  const to_component = connection.to_component;
  const from_is_passive = from_component?.type === 'passive';
  const to_is_passive = to_component?.type === 'passive';
  const from_is_controller = is_microcontroller_like(from_component);
  const to_is_controller = is_microcontroller_like(to_component);

  if (from_is_passive && !to_is_passive) {
    return {
      start_component: to_component,
      start_pin: connection.to_pin,
      end_component: from_component,
      end_pin: connection.from_pin,
    };
  }

  if (!from_is_passive && to_is_passive) {
    return {
      start_component: from_component,
      start_pin: connection.from_pin,
      end_component: to_component,
      end_pin: connection.to_pin,
    };
  }

  if (!from_is_controller && to_is_controller) {
    return {
      start_component: from_component,
      start_pin: connection.from_pin,
      end_component: to_component,
      end_pin: connection.to_pin,
    };
  }

  if (from_is_controller && !to_is_controller) {
    return {
      start_component: to_component,
      start_pin: connection.to_pin,
      end_component: from_component,
      end_pin: connection.from_pin,
    };
  }

  return {
    start_component: from_component,
    start_pin: connection.from_pin,
    end_component: to_component,
    end_pin: connection.to_pin,
  };
};

export const generate_wiring_steps = (project) => {
  const context = build_project_context(project);

  return [...context.resolved_connections]
    .sort((left, right) => get_step_priority(left) - get_step_priority(right))
    .map((connection, index) => {
      const ordered = order_step_endpoints(connection);

      return {
        id: `step-${index + 1}`,
        order: index + 1,
        text: `Connect ${get_connection_label(ordered.start_component, ordered.start_pin)} to ${get_connection_label(ordered.end_component, ordered.end_pin)}.`,
        category:
          connection.wireColorRole === 'power'
            ? 'power'
            : connection.wireColorRole === 'ground'
              ? 'ground'
              : 'signal',
        related_items: {
          component_ids: [connection.from_pin.component_id, connection.to_pin.component_id],
          pin_ids: [String(connection.from_pin.id), String(connection.to_pin.id)],
          wire_ids: [connection.id],
        },
      };
    });
};
